import { booleanAttribute, ChangeDetectionStrategy, Component, computed, forwardRef, input, model, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { IconComponent } from '../icon/icon.component';
import { Lock } from '../icon/icons';

let nextId = 0;

/**
 * Campo con etiqueta visible. Funciona con `[(value)]` y con formularios reactivos. La ayuda y el error
 * se ligan con `aria-describedby`, y el error también se dice con `aria-invalid`, no sólo con color.
 *
 * `locked` es el dato que define la empresa (nombre, correo…): se lee, se enfoca y se anuncia como de
 * sólo lectura, con el candado a la vista.
 */
@Component({
  selector: 'ui-text-field',
  imports: [IconComponent],
  template: `
    <label class="label" [attr.for]="inputId">{{ label() }}</label>
    <div class="control" [class.locked]="locked()" [class.invalid]="!!error()">
      <input
        class="input"
        [id]="inputId"
        [type]="type()"
        [value]="value()"
        [readOnly]="locked()"
        [disabled]="isDisabled()"
        [attr.inputmode]="inputMode() ?? null"
        [attr.autocomplete]="autocomplete() ?? null"
        [attr.placeholder]="placeholder() ?? null"
        [attr.required]="required() ? '' : null"
        [attr.aria-invalid]="error() ? 'true' : null"
        [attr.aria-describedby]="describedBy()"
        (input)="onInput($event)"
        (blur)="onTouched()"
      />
      @if (locked()) {
        <ui-icon class="lock" [icon]="lockIcon" size="1rem" />
      }
    </div>
    @if (hint(); as hintText) {
      <p class="hint" [id]="hintId">{{ hintText }}</p>
    }
    @if (error(); as errorText) {
      <p class="error" [id]="errorId">{{ errorText }}</p>
    }
  `,
  styleUrl: './text-field.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => TextFieldComponent), multi: true }],
})
export class TextFieldComponent implements ControlValueAccessor {
  readonly label = input.required<string>();
  readonly value = model('');
  readonly type = input<'text' | 'email' | 'tel' | 'password' | 'number'>('text');
  readonly inputMode = input<string>();
  readonly autocomplete = input<string>();
  readonly placeholder = input<string>();
  readonly hint = input<string>();
  readonly error = input<string | null>();
  readonly locked = input(false, { transform: booleanAttribute });
  readonly required = input(false, { transform: booleanAttribute });
  readonly disabled = input(false, { transform: booleanAttribute });

  private readonly formDisabled = signal(false);
  protected readonly isDisabled = computed(() => this.disabled() || this.formDisabled());

  private readonly id = nextId++;
  protected readonly inputId = `ui-field-${this.id}`;
  protected readonly hintId = `ui-field-${this.id}-hint`;
  protected readonly errorId = `ui-field-${this.id}-error`;
  protected readonly lockIcon = Lock;

  protected readonly describedBy = computed(() => {
    const ids = [this.hint() ? this.hintId : null, this.error() ? this.errorId : null].filter(Boolean);
    return ids.length ? ids.join(' ') : null;
  });

  private onChange: (value: string) => void = () => undefined;
  protected onTouched: () => void = () => undefined;

  protected onInput(event: Event): void {
    const next = (event.target as HTMLInputElement).value;
    this.value.set(next);
    this.onChange(next);
  }

  writeValue(value: string | null): void {
    this.value.set(value ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formDisabled.set(isDisabled);
  }
}
